import { useCallback, useEffect, useState } from 'react';

const EMPTY_ARRAY = [];

/**
 * Fetch a list into local state, guarding against a stale response
 * landing after the component unmounts or `deps` change again mid-flight.
 *
 * `fetchFn` is called with no arguments and must return a promise that
 * resolves to the array to store. Its identity is not tracked as a
 * dependency — pass a stable callback (`useCallback`) and control
 * re-fetching entirely through `deps`, mirroring the `let cancelled`
 * effect this replaces.
 *
 * @param {() => Promise<Array>} fetchFn
 * @param {Array} deps          effect dependency list — re-fetches on change
 * @param {Object} [options]
 * @param {Array} [options.initial=[]]  value held before the first fetch resolves
 * @returns {{ items: Array, loading: boolean, error: *, reload: () => void }}
 */
export default function useFetchedList(fetchFn, deps, { initial = EMPTY_ARRAY } = {}) {
  const [items, setItems] = useState(initial);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => setReloadToken(token => token + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchFn()
      .then(result => {
        if (cancelled) return;
        setItems(result);
        setError(null);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [...deps, reloadToken]);

  return { items, loading, error, reload };
}
